// hooks/useActivityFeed.ts
import { useMemo } from "react";
import { useInfiniteQuery } from "@tanstack/react-query";
import { ActivityGroup } from "types/crm/activity";

export const mergeActivityGroups = (
  groupPages: ActivityGroup[][],
): ActivityGroup[] => {
  const merged: ActivityGroup[] = [];

  groupPages
    .flat()
    .filter((group): group is ActivityGroup => !!group)
    .forEach((group) => {
      const existing = merged.find((g) => g.date === group.date);
      if (existing) {
        existing.activities = [...existing.activities, ...group.activities];
      } else {
        merged.push({
          date: group.date,
          label: group.label,
          activities: [...group.activities],
        });
      }
    });

  return merged;
};

// Structural shape shared by activity endpoints — no relation to Page<T>,
// since activity responses don't carry a `content` array.
interface ActivityPage {
  groups: ActivityGroup[];
  last: boolean;
  page: number;
  totalElements: number;
}

export function useActivityFeed(
  queryKey: readonly unknown[],
  queryFn: (pageParam: number) => Promise<ActivityPage>,
  enabled: boolean,
) {
  const { data, isLoading, isFetchingNextPage, hasNextPage, fetchNextPage, error } =
    useInfiniteQuery({
      queryKey,
      queryFn: ({ pageParam }) => queryFn(pageParam as number),
      initialPageParam: 0,
      getNextPageParam: (lastPage: ActivityPage) =>
        lastPage.last ? undefined : lastPage.page + 1,
      enabled,
    });

  const groups = useMemo(
    () => mergeActivityGroups(data?.pages.map((p) => p.groups) ?? []),
    [data],
  );

  const totalElements = data?.pages[0]?.totalElements ?? 0;

  return {
    groups,
    isLoading,
    isFetchingMore: isFetchingNextPage,
    hasMore: !!hasNextPage,
    onLoadMore: fetchNextPage,
    totalElements,
    error,
  };
}